import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState, memo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButtons } from "@/components/ShareButtons";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useTranslation } from "react-i18next";

interface ArtworkCardProps {
  id: string;
  slug?: string;
  title: string;
  artistName: string;
  price: number;
  imageUrl: string;
  category: string;
  artworkType: "original" | "print" | "print-on-demand";
  stockQuantity?: number;
  showFavorite?: boolean;
  showShare?: boolean;
  minVariantPrice?: number | null;
}

export const ArtworkCard = memo(({
  id,
  slug,
  title,
  artistName,
  price,
  imageUrl,
  category,
  artworkType,
  stockQuantity = 0,
  showFavorite = true,
  showShare = true,
  minVariantPrice,
}: ArtworkCardProps) => {
  const artworkUrl = `/artwork/${slug || id}`;
  const { t } = useTranslation('common');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  const displayPrice = artworkType === "print-on-demand" && minVariantPrice
    ? minVariantPrice
    : price;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <Link to={artworkUrl} className="h-full block">
      <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col h-full active:scale-[0.98]">
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-muted to-muted/50 flex-shrink-0">
        <OptimizedImage
          src={imageUrl}
          alt={title}
          className="transition-all duration-700 group-hover:scale-105 group-hover:brightness-105"
          loading="lazy"
        />
        {(showFavorite || showShare) && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {showFavorite && (
              <div className="bg-background/90 backdrop-blur-md rounded-lg shadow-lg">
                <FavoriteButton artworkId={id} />
              </div>
            )}
            {showShare && (
              <div className="bg-background/90 backdrop-blur-md rounded-lg shadow-lg">
                <ShareButtons 
                  title={`${title} av ${artistName}`}
                  url={artworkUrl}
                  description={t('common.checkOut')}
                />
              </div>
            )}
          </div>
        )}
        {stockQuantity === 0 && (
          <div className="absolute top-2 left-2">
            <Badge variant="destructive" className="bg-destructive/90 backdrop-blur-md shadow-lg text-[10px] sm:text-xs">
              {t('common.soldOut')}
            </Badge>
          </div>
        )}
      </div>
      <div className="p-2.5 sm:p-3 flex flex-col flex-1 gap-1.5 bg-gradient-to-b from-card to-card/50">
        <div className="min-w-0">
          <h3 className="font-semibold text-sm text-foreground line-clamp-1 leading-tight group-hover:text-primary transition-colors">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{artistName}</p>
        </div>

        <div className="mt-auto">
          {artworkType === "print-on-demand" ? (
            <p className="font-bold text-lg sm:text-xl text-primary leading-tight">
              {t('common.from')} {displayPrice.toLocaleString("sv-SE")} kr
            </p>
          ) : (
            <p className="font-bold text-lg sm:text-xl text-primary leading-tight">
              {displayPrice.toLocaleString("sv-SE")} kr
            </p>
          )}
        </div>
      </div>
    </Card>
    </Link>
  );
});

ArtworkCard.displayName = 'ArtworkCard';
